import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Plus, Trash2, FileBadge } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useContacts, useCreateInvoice, useUpdateInvoice } from '@/hooks/useInvoices';
import { useBetonProduits } from '@/hooks/useBetonCatalog';
import { useCommandesImpression } from '@/hooks/useTextprint';
import { useAuthStore } from '@/stores/authStore';
import { operationsApi } from '@/api/operations';
import { extractError } from '@/api/client';
import { formatFCFA } from '@/utils/currency';
import { INVOICE_TYPE_LABELS, type Invoice, type InvoiceType } from '@/types';
import { demander, informer } from '@/components/ui/Fenetres';
import { toast } from '@/stores/toastStore';

interface Props {
  open:     boolean;
  onClose:  () => void;
  /** Si fournie, le dialog passe en mode édition (uniquement valide si invoice.status === 'proforma') */
  invoice?: Invoice;
}

interface LineForm {
  produitId:   string;  // '' = ligne libre
  designation: string;
  quantite:    string;
  prixUnitaire: string; // FCFA (pas centimes)
}

const emptyLine = (): LineForm => ({ produitId: '', designation: '', quantite: '1', prixUnitaire: '' });

const todayISO = () => new Date().toISOString().slice(0, 10);
const todayPlus = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export const InvoiceFormDialog = ({ open, onClose, invoice }: Props) => {
  const isEdit = !!invoice;
  const filiale = useAuthStore((s) => s.filiale);
  const metier  = filiale?.metier ?? 'generic';

  const [invoiceType, setInvoiceType] = useState<InvoiceType>('client');
  const [contactId, setContactId]     = useState('');
  const [commandeSourceId, setCommandeSourceId] = useState(''); // Textprint : commande source
  const [issueDate, setIssueDate]     = useState(todayISO());
  const [dueDate, setDueDate]         = useState(todayPlus(30));
  const [lines, setLines]             = useState<LineForm[]>([emptyLine()]);
  const [tvaRate, setTvaRate]         = useState(18);
  const [remisePct, setRemisePct]     = useState(0);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [error, setError]             = useState<string | null>(null);

  const { data: contactsData, isLoading: contactsLoading } = useContacts({
    contactType: invoiceType === 'client' ? 'client' : 'supplier',
    limit: 200,
  });
  // Catalogue Béton : chargé uniquement si métier=beton (sinon endpoint vide pour autres tenants)
  const { data: produitsData } = useBetonProduits({ actif: true, limit: 200 });
  // Textprint : commandes facturables (toutes sauf annulées/facturées déjà)
  const { data: commandesImpData } = useCommandesImpression({ limit: 200 });
  const create = useCreateInvoice();
  const update = useUpdateInvoice();

  useEffect(() => {
    if (!open) return;
    if (invoice) {
      // ── Mode édition : précharge depuis l'invoice
      setInvoiceType(invoice.invoiceType);
      setContactId(invoice.contactId);
      setIssueDate(invoice.issueDate.slice(0, 10));
      setDueDate(invoice.dueDate.slice(0, 10));
      setLines(invoice.lines.map((l) => ({
        produitId:    l.produitId ?? '',
        designation:  l.designation,
        quantite:     l.quantite,
        prixUnitaire: String(Number(BigInt(l.prixUnitaireCents)) / 100),
      })));
      // Calcul du taux TVA appliqué (basé sur HT brut, indicatif)
      const ht  = Number(BigInt(invoice.subtotalCents)) / 100;
      const tva = Number(BigInt(invoice.taxCents))      / 100;
      const remise = parseFloat(invoice.remisePct ?? '0');
      const htNet  = ht * (100 - remise) / 100;
      setTvaRate(htNet > 0 ? Math.round((tva / htNet) * 100 * 10) / 10 : 18);
      setRemisePct(remise);
      setPaymentTerms(invoice.paymentTerms ?? '');
    } else {
      setInvoiceType('client');
      setContactId('');
      setCommandeSourceId('');
      setIssueDate(todayISO());
      setDueDate(todayPlus(30));
      setLines([emptyLine()]);
      setTvaRate(18);
      setRemisePct(0);
      setPaymentTerms('');
    }
    setError(null);
  }, [open, invoice]);

  // En création, le changement de type reset le contact (listes différentes).
  // En édition, on garde le contact existant (déjà chargé via invoice).
  useEffect(() => {
    if (!isEdit) setContactId('');
  }, [invoiceType, isEdit]);

  const produits      = produitsData?.data ?? [];
  const produitsIndex = useMemo(() => new Map(produits.map((p) => [p.id, p])), [produits]);
  const contacts      = contactsData?.data ?? [];

  // Textprint — commandes facturables (exclut déjà-facturée, annulée, brouillon)
  const commandesImpression = useMemo(() => {
    if (metier !== 'impression') return [];
    const all = commandesImpData?.data ?? [];
    return all.filter((c) => !['facturee', 'annulee', 'brouillon'].includes(c.statut as any));
  }, [metier, commandesImpData]);

  /** Charge les lignes d'une CommandeImpression dans le formulaire facture (Textprint) */
  const loadFromCommandeImpression = (commandeId: string) => {
    const cmd = commandesImpression.find((c) => c.id === commandeId);
    if (!cmd) return;
    setCommandeSourceId(commandeId);
    // Auto-fill client si pas déjà choisi (ou si différent)
    if (cmd.clientId) setContactId(cmd.clientId);
    // Mapper les lignes
    const lignesFormulaire: LineForm[] = cmd.lignes.length > 0
      ? cmd.lignes.map((l) => ({
          produitId:    '',  // pas de FK produit catalogue côté Textprint
          designation:  l.designation + (l.unite ? ` (${l.unite})` : ''),
          quantite:     String(l.quantite),
          prixUnitaire: String(l.prixUnitaireCents / 100),
        }))
      : [emptyLine()];
    setLines(lignesFormulaire);
  };

  const updateLine = (idx: number, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const next = { ...l, ...patch };
      // Auto-remplissage si choix d'un produit du catalogue
      if (patch.produitId !== undefined && patch.produitId) {
        const p = produitsIndex.get(patch.produitId);
        if (p) {
          next.designation  = p.designation;
          next.prixUnitaire = String(Number(BigInt(p.prixVenteCents)) / 100);
        }
      }
      return next;
    }));
  };

  const addLine    = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));

  // Sprint LB-E v2 : auto-application tarif contrat cadre client+produit
  const applyTarifContrat = async (idx: number, produitId: string) => {
    if (!contactId || !produitId) return;
    try {
      const prix = await operationsApi.beton.getPrixApplicable(contactId, produitId);
      if (prix.source === 'contrat_cadre') {
        setLines((prev) => prev.map((l, i) =>
          i !== idx ? l : { ...l, prixUnitaire: String(prix.prixHTCents / 100) }
        ));
        informer(`Tarif contrat ${prix.contratRef} appliqué`, `${(prix.prixHTCents / 100).toLocaleString('fr-FR')} FCFA${prix.remisePct > 0 ? ` (remise -${prix.remisePct}%)` : ''}`);
      } else {
        informer('Prix catalogue maintenu', 'Aucun tarif contrat cadre actif pour ce client et ce produit.');
      }
    } catch (err) {
      toast.error('Tarif contrat non lu', extractError(err).message);
    }
  };

  const totals = useMemo(() => {
    let totalHtBrut = 0;
    const enriched = lines.map((l) => {
      const q  = parseFloat(l.quantite     || '0');
      const pu = parseFloat(l.prixUnitaire || '0');
      const total = q * pu;
      totalHtBrut += total;
      return { totalLine: total };
    });
    // Ticket SIMGROUP-2026-002 M-04 : FCFA n'a pas de subdivision.
    // On arrondit tous les montants au franc entier avant affichage/persistance.
    // La TVA absorbe l'écart d'arrondi pour que HT + TVA = TTC au franc près.
    const remiseMontant   = Math.round(totalHtBrut * remisePct / 100);
    const totalHtNet      = totalHtBrut - remiseMontant;
    const ttc             = Math.round(totalHtNet * (1 + tvaRate / 100));
    const tva             = ttc - totalHtNet;  // absorbe l'écart d'arrondi
    return { totalHtBrut, remiseMontant, totalHtNet, tva, ttc, enriched };
  }, [lines, tvaRate, remisePct]);

  const canSubmit =
    contactId &&
    issueDate &&
    dueDate &&
    lines.length > 0 &&
    lines.every((l) =>
      l.designation.trim() &&
      parseFloat(l.quantite || '0') > 0 &&
      parseFloat(l.prixUnitaire || '0') >= 0
    ) &&
    totals.totalHtBrut > 0 &&
    remisePct >= 0 && remisePct < 100;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const payload = {
      invoiceType,
      contactId,
      issueDate,
      dueDate,
      lines: lines.map((l) => ({
        produitId:         l.produitId || undefined,
        designation:       l.designation.trim(),
        quantite:          parseFloat(l.quantite || '0'),
        prixUnitaireCents: Math.round(parseFloat(l.prixUnitaire || '0') * 100),
      })),
      taxCents:  Math.round(totals.tva * 100),
      remisePct: isEdit ? remisePct : 0, // remise applicable uniquement en édition (v2+)
      currency:  'FCFA',
      paymentTerms: paymentTerms.trim() || undefined,
    };
    try {
      if (isEdit && invoice) {
        await update.mutateAsync({ id: invoice.id, payload });
      } else {
        await create.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  const submitting = create.isPending || update.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit && invoice ? `Modifier proforma — ${invoice.reference}` : 'Nouvelle facture proforma'}
      subtitle={isEdit
        ? 'Modification possible tant que la proforma n\'est pas validée'
        : 'Créée en statut PROFORMA — convertir en facture finale après validation client'}
      size="xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Type *">
            <select
              value={invoiceType}
              onChange={(e) => setInvoiceType(e.target.value as InvoiceType)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
            >
              {(Object.keys(INVOICE_TYPE_LABELS) as InvoiceType[]).map((t) => (
                <option key={t} value={t}>{INVOICE_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </Field>
          <Field label={invoiceType === 'client' ? 'Client *' : 'Fournisseur *'}>
            {contactsLoading ? (
              <div className="rounded-md border border-gray-200 px-3 py-2 text-xs text-gray-400">Chargement…</div>
            ) : contacts.length === 0 ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                Aucun {invoiceType === 'client' ? 'client' : 'fournisseur'} en DB. Créez-en un via le module CRM.
              </div>
            ) : (
              <select
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
                required
              >
                <option value="">— Sélectionner —</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.companyName ? `${c.companyName} (${c.fullName})` : c.fullName}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date d'émission *">
            <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} required />
          </Field>
          <Field label="Date d'échéance *">
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required />
          </Field>
        </div>

        {/* ─── Conditions de paiement (déplacée en haut pour visibilité) ─── */}
        {/* Couleurs alignées sur la filiale connectée via variables CSS --p / --p-soft / --p-dark
            (LeadBeton = vert, Textprint = bleu, Royal LT = ambre, etc.) */}
        <div
          data-testid="payment-terms-section"
          className="rounded-lg border-2 p-4 space-y-3"
          style={{
            borderColor: 'var(--p, #1F845C)',
            background: 'var(--p-soft, #E8F7F1)',
          }}
        >
          <div className="flex items-center justify-between">
            <label
              className="flex items-center gap-2 text-sm font-semibold"
              style={{ color: 'var(--p-deep, #0A3D28)' }}
            >
              <span className="text-lg">💳</span>
              Conditions de paiement
              <span className="text-[11px] font-normal" style={{ color: 'var(--p-dark, #155E42)' }}>
                (affichées sur la proforma PDF)
              </span>
            </label>
            <span
              className="text-[11px] font-medium bg-white/70 rounded-full px-2 py-0.5"
              style={{ color: 'var(--p-dark, #155E42)' }}
            >
              {paymentTerms.length} / 500
            </span>
          </div>
          {/* Presets rapides */}
          <div className="flex flex-wrap gap-1.5">
            {[
              '50% à la commande, solde à la livraison',
              '30 jours fin de mois',
              '60 jours date facture',
              'Paiement à la livraison',
              'Virement bancaire sous 15 jours',
              'Acompte 30% + solde à réception',
            ].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setPaymentTerms(preset)}
                className="rounded-full border bg-white px-2.5 py-1 text-[11px] text-gray-700 hover:text-white transition-colors"
                style={{
                  borderColor: 'var(--p-light, #2EAD7A)',
                  ['--hover-bg' as any]: 'var(--p, #1F845C)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--p, #1F845C)';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.color = '';
                }}
                title={`Utiliser : ${preset}`}
              >
                + {preset}
              </button>
            ))}
          </div>
          <textarea
            rows={3}
            maxLength={500}
            value={paymentTerms}
            onChange={(e) => setPaymentTerms(e.target.value)}
            placeholder='Ex: "50% à la commande, 50% à la livraison — Virement Ecobank compte 00123456 ou Mobile Money +225 07 XX XX XX XX"'
            className="w-full rounded-md border bg-white/80 px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2"
            style={{ borderColor: 'var(--p-light, #2EAD7A)' }}
          />
          <p className="text-[11px]" style={{ color: 'var(--p-dark, #155E42)' }}>
            💡 Cliquez un preset pour remplir rapidement, puis adaptez. Ce texte apparaît en bas de la proforma imprimée envoyée au client.
          </p>
        </div>

        {/* Textprint — Charger lignes depuis une commande existante */}
        {metier === 'impression' && invoiceType === 'client' && !isEdit && (
          <div className="rounded-md border border-blue-200 bg-blue-50/40 p-3">
            <label className="mb-1 block text-xs font-medium text-blue-900">
              Charger depuis une commande Textprint (recommandé)
            </label>
            {commandesImpression.length === 0 ? (
              <div className="text-xs text-gray-500">
                Aucune commande facturable. Créer une commande dans Opérations → Commandes d'impression.
              </div>
            ) : (
              <select
                value={commandeSourceId}
                onChange={(e) => e.target.value ? loadFromCommandeImpression(e.target.value) : setCommandeSourceId('')}
                className="flex h-10 w-full rounded-md border border-blue-200 bg-white px-3 text-sm"
              >
                <option value="">— Sélectionner une commande pour pré-remplir —</option>
                {commandesImpression.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.reference} · {c.client?.companyName ?? c.client?.fullName ?? '(sans client)'} ·
                    {' '}{c.lignes.length} ligne{c.lignes.length > 1 ? 's' : ''} ·
                    {' '}{formatFCFA(Number(BigInt(c.totalHtCents)))}
                  </option>
                ))}
              </select>
            )}
            {commandeSourceId && (
              <p className="mt-2 text-[11px] text-blue-700">
                ✓ Lignes auto-remplies. Vous pouvez encore ajouter/modifier des lignes libres ci-dessous.
              </p>
            )}
          </div>
        )}

        {/* Lignes */}
        <div>
          <label className="mb-2 block text-xs font-medium text-gray-600">Lignes de facturation *</label>
          <div className="overflow-x-auto rounded-md border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  {metier === 'beton' && (
                    <th className="px-3 py-2 text-left font-medium" style={{ width: '22%' }}>Produit catalogue</th>
                  )}
                  <th className="px-3 py-2 text-left font-medium">Désignation</th>
                  <th className="px-3 py-2 text-right font-medium" style={{ width: '80px' }}>Qté</th>
                  <th className="px-3 py-2 text-right font-medium" style={{ width: '130px' }}>PU HT (FCFA)</th>
                  <th className="px-3 py-2 text-right font-medium" style={{ width: '130px' }}>Total HT</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map((line, idx) => (
                  <tr key={idx}>
                    {metier === 'beton' && (
                      <td className="px-3 py-2">
                        <select
                          value={line.produitId}
                          onChange={(e) => updateLine(idx, { produitId: e.target.value })}
                          className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-xs"
                          title="Choisir un produit catalogue (auto-remplit) ou laisser vide pour une ligne libre"
                        >
                          <option value="">— Ligne libre —</option>
                          {produits.map((p) => (
                            <option key={p.id} value={p.id}>{p.code}</option>
                          ))}
                        </select>
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <Input
                        value={line.designation}
                        onChange={(e) => updateLine(idx, { designation: e.target.value })}
                        placeholder={metier === 'impression' ? 'ex: T-shirt brodé 250g/m² noir' : 'Désignation'}
                        className="h-9"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number" min="0" step="0.01"
                        value={line.quantite}
                        onChange={(e) => updateLine(idx, { quantite: e.target.value })}
                        className="h-9 text-right tabular-nums"
                      />
                      {/* Ticket SIMGROUP-2026-003 §4.1 — fix ALLECHI 7,1M FCFA.
                          Si produit vendu à la pièce ET ayant une longueur d'élément,
                          on montre l'équivalent mL et un bouton pour saisir en mL
                          (auto-arrondit au nombre d'éléments supérieur). */}
                      {(() => {
                        const p = line.produitId ? produitsIndex.get(line.produitId) : null;
                        const longueur = p?.longueurM ? parseFloat(p.longueurM) : 0;
                        const isSoldByPiece = p && ['piece', 'U', 'unite'].includes(p.uniteVente ?? '');
                        if (!isSoldByPiece || longueur <= 0) return null;
                        const qtePieces = parseFloat(line.quantite || '0');
                        const cumulMl = qtePieces * longueur;
                        return (
                          <div className="mt-1 text-[10px] text-gray-500 leading-tight">
                            <span className="font-semibold text-amber-700">{p.uniteVente} de {longueur} m</span>
                            {qtePieces > 0 && <span className="ml-1">· = {cumulMl.toFixed(2).replace('.', ',')} mL cumulés</span>}
                            <button
                              type="button"
                              className="ml-2 text-blue-600 hover:underline"
                              title="Convertir un besoin exprimé en mètres linéaires en nombre de pièces (arrondi à l'entier supérieur)"
                              onClick={async () => {
                                const raw = await demander.texte({
                                  titre: `${p.code} — besoin en mètres linéaires`,
                                  message: `1 pièce = ${longueur} m. Arrondi à l'entier supérieur (pas de coupe).`,
                                  libelle: 'Mètres linéaires', min: 1, confirmer: 'Convertir en pièces',
                                });
                                if (raw === null) return;
                                const ml = parseFloat(raw.replace(',', '.'));
                                if (!Number.isFinite(ml) || ml <= 0) return;
                                const nbPieces = Math.ceil(ml / longueur);
                                updateLine(idx, { quantite: String(nbPieces) });
                              }}
                            >
                              Saisir en mL
                            </button>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number" min="0" step="1"
                        value={line.prixUnitaire}
                        onChange={(e) => updateLine(idx, { prixUnitaire: e.target.value })}
                        className="h-9 text-right tabular-nums"
                      />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-700">
                      {formatFCFA(Math.round(totals.enriched[idx].totalLine * 100))}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {line.produitId && contactId && (
                        <button
                          type="button"
                          onClick={() => void applyTarifContrat(idx, line.produitId)}
                          className="text-blue-600 hover:text-blue-800 mr-2"
                          title="Appliquer tarif contrat cadre si actif pour ce client"
                        >
                          <FileBadge className="size-4 inline" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        disabled={lines.length <= 1}
                        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30"
                        title={lines.length <= 1 ? 'Minimum 1 ligne' : 'Supprimer'}
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button type="button" variant="secondary" size="sm" className="mt-2" onClick={addLine}>
            <Plus className="size-4" /> Ajouter une ligne
          </Button>
        </div>

        {/* Totaux */}
        <div className="rounded-md bg-gray-50 p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Total HT brut</span>
            <span className="font-semibold tabular-nums text-gray-900">{formatFCFA(Math.round(totals.totalHtBrut * 100))}</span>
          </div>

          {/* Remise : visible uniquement en mode édition (= passage à v2+) */}
          {isEdit && (
            <>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="font-medium text-orange-700">Remise</span>
                  <Input
                    type="number" min="0" max="99" step="0.5"
                    value={remisePct}
                    onChange={(e) => setRemisePct(parseFloat(e.target.value) || 0)}
                    className="h-7 w-16 text-right tabular-nums"
                  />
                  <span className="text-xs text-gray-500">%</span>
                </div>
                <span className="tabular-nums text-orange-700">- {formatFCFA(Math.round(totals.remiseMontant * 100))}</span>
              </div>
              {remisePct > 0 && (
                <div className="flex items-center justify-between text-sm border-t border-gray-200 pt-2">
                  <span className="text-gray-600">Total HT net</span>
                  <span className="font-semibold tabular-nums text-gray-900">{formatFCFA(Math.round(totals.totalHtNet * 100))}</span>
                </div>
              )}
            </>
          )}

          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <span>TVA</span>
              <Input
                type="number" min="0" max="100" step="0.5"
                value={tvaRate}
                onChange={(e) => setTvaRate(parseFloat(e.target.value) || 0)}
                className="h-7 w-16 text-right tabular-nums"
              />
              <span className="text-xs text-gray-500">%</span>
            </div>
            <span className="tabular-nums text-gray-700">{formatFCFA(Math.round(totals.tva * 100))}</span>
          </div>
          <div className="flex items-center justify-between border-t border-gray-200 pt-2">
            <span className="text-xs uppercase tracking-wider text-gray-500">Total TTC</span>
            <span className="text-xl font-bold tabular-nums text-gray-900">{formatFCFA(Math.round(totals.ttc * 100))}</span>
          </div>
          {!isEdit && (
            <p className="border-t border-gray-200 pt-2 text-[11px] text-gray-400">
              Astuce : un taux de remise sera disponible à la <strong>1ère modification</strong> de la proforma (passage à v2).
            </p>
          )}
        </div>

        {/* Erreur dupliquée près du bouton submit (le dialog peut être scrollé,
            l'erreur en haut serait invisible sinon). */}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            ⚠ {error}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" disabled={!canSubmit} loading={submitting}>
            {isEdit ? 'Enregistrer les modifications' : 'Créer la proforma'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
